import { finnhubKey, massiveKey, settingsStore } from '@/settings/settingsStore';
import type { AssetClass, Market } from '@/types';
import { finnhubSearch, type SymbolHit } from './finnhub';
import { massiveSearch } from './massive';
import { ensureSymbolIndex, searchSymbolIndex, symbolIndexReady } from './symbolIndex';
import { twelvedataSearch } from './twelvedata';

export interface SearchHit extends SymbolHit {
  assetClass: 'stock' | 'etf';
}

const MAX_HITS = 8;

function classify(type: string): 'stock' | 'etf' {
  return /etf|etp|fund|trust/i.test(type) ? 'etf' : 'stock';
}

/**
 * Relevance tier of a hit for an upper-cased query; lower is better. Symbol matches outrank name matches, and a
 * share class counts as a symbol match ("BRK" → BRK.A, BRK.B) so the classes surface without typing the suffix.
 */
export function relevance(hit: SymbolHit, q: string): number {
  const sym = hit.symbol.toUpperCase();
  const base = sym.split('.')[0];
  if (sym === q) return 0;
  if (base === q) return 1;
  if (sym.startsWith(q)) return 2;
  const name = hit.name.toUpperCase();
  if (name.startsWith(q)) return 3;
  if (name.includes(q)) return 4;
  return 5;
}

/** Ranks provider hits by relevance, then by the asset class the user is adding, then provider order; dedupes; cuts to MAX_HITS. */
export function rankHits(hits: SymbolHit[], query: string, wanted: AssetClass): SearchHit[] {
  const q = query.trim().toUpperCase();
  const seen = new Set<string>();
  return hits
    .filter((h) => (seen.has(h.symbol) ? false : (seen.add(h.symbol), true)))
    .map((h, i) => ({ hit: { ...h, assetClass: classify(h.type) }, score: relevance(h, q), i }))
    .sort((a, b) => a.score - b.score || Number(b.hit.assetClass === wanted) - Number(a.hit.assetClass === wanted) || a.i - b.i)
    .slice(0, MAX_HITS)
    .map((x) => x.hit);
}

/** Symbol search through whichever equity source the user configured; empty when there is none. */
export async function searchSymbols(query: string, wanted: AssetClass, market: Market = 'US', signal?: AbortSignal): Promise<SearchHit[]> {
  const q = query.trim();
  if (!q) return [];
  if (market === 'CA') {
    // Public listings: works with no key at all; the remote search covers the moment before the index is loaded.
    void ensureSymbolIndex('CA');
    let hits: SymbolHit[] = searchSymbolIndex(q, 'CA');
    if (!symbolIndexReady('CA') || hits.length === 0) hits = [...hits, ...(await twelvedataSearch(q, signal))];
    return rankHits(hits, q, wanted);
  }
  const settings = settingsStore.get();
  const fh = finnhubKey(settings);
  const mv = massiveKey(settings);
  let hits: SymbolHit[] = [];
  if (fh) {
    void ensureSymbolIndex('US', fh);
    hits = searchSymbolIndex(q, 'US');
    // Finnhub's /search matches whole symbols and name words only (no ticker prefixes), so it is the
    // fallback while the local index is still loading or when the index has nothing for the query.
    if (!symbolIndexReady('US') || hits.length === 0) hits = [...hits, ...(await finnhubSearch(q, fh, signal))];
  } else if (mv) hits = await massiveSearch(q, mv, signal);
  return rankHits(hits, q, wanted);
}
